import { useFormik } from 'formik'
import React, { ChangeEvent, useEffect, useState } from 'react'
import ModalComponent from '..'
import { useModalControl } from '../../../hooks/useModalControl'
import { ManualContent } from '../../../redux-toolkits/home-page/homePage.type'
import { AddExploreSchema } from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import { ImageBanner } from '../../images/imageBanner'
import { EditInput } from '../../inputs/editInput'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

export const AddExploreForm = ({
  onSuccessCallback = () => {},
  content,
  open,
  mode,
  loading,
  proceedText = 'Continue',
}: {
  onSuccessCallback?: (val: Omit<ManualContent, '_id'>) => void
  content?: ManualContent
  open: boolean
  mode: 'edit' | 'create'
  loading?: boolean
  proceedText?: string
}) => {
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [editIndex, setEditIndex] = useState<number>()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }
    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)
    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      title: content?.title ?? '',
      image: content?.image ?? '',
      linkUrl: content?.linkUrl ?? '',
      description: content?.description ?? '',
    },
    enableReinitialize: true,
    validationSchema: AddExploreSchema,
    onSubmit: (values) => {
      onSuccessCallback(values)
    },
  })

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <div>
      <form
        className="flex flex-col items-center justify-center gap-4"
        onSubmit={handleSubmit}
      >
        <>
          {values.image ? (
            <ImageBanner
              imageUrl={values.image}
              loading={false}
              className="rounded-md max-h-[192px] bg-cover"
              errors={errors.image ? errors.image : ''}
              onImageEdit={(e) => {
                handleFileChange(e)
              }}
              editIconPos="top-right"
            />
          ) : (
            <ImageUploadButton
              handleChange={(e) => {
                handleFileChange(e)
              }}
              showText
              icon={'pic-icon'}
              iconDim={52}
              text={`Upload image`}
              className="
              !relative !w-full
              !m-0 !top-0 !right-0
              !h-fit
              !rounded-lg
              !col-span-full
              "
            />
          )}
        </>

        <EditInput
          title="Title"
          value={values.title}
          onChange={handleChange}
          type="text"
          placeholder="Title"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 1}
          onEditClick={() => {
            setEditIndex(1)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('title', '')
            } else {
              setFieldValue('title', content?.title)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="title"
          mode={mode === 'create' ? 'edit' : 'edit'}
          errors={touched.title && errors.title ? errors.title : ''}
        />

        <EditInput
          title="URL"
          value={values.linkUrl}
          onChange={handleChange}
          type="text"
          placeholder="URL"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 2}
          onEditClick={() => {
            setEditIndex(2)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('linkUrl', '')
            } else {
              setFieldValue('linkUrl', content?.linkUrl)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="linkUrl"
          mode={mode === 'create' ? 'edit' : 'edit'}
          errors={touched.linkUrl && errors.linkUrl ? errors.linkUrl : ''}
        />

        <EditInput
          title="Description"
          value={values.description}
          onChange={handleChange}
          type="textarea"
          placeholder="Description"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 3}
          onEditClick={() => {
            setEditIndex(3)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('description', '')
            } else {
              setFieldValue('description', content?.description)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="description"
          mode={mode === 'create' ? 'edit' : 'edit'}
          errors={
            touched.description && errors.description ? errors.description : ''
          }
        />

        <Button
          label={proceedText}
          type="submit"
          className="mt-2 w-full"
          disabled={loading}
          loading={loading}
          primary
        />
      </form>

      {uploadedImage ? (
        <ImageUploadPreviewModal
          image={uploadedImage as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedImage(undefined)
          }}
          onSuccessCallback={(url) => {
            setFieldValue('image', url)
          }}
        />
      ) : null}
    </div>
  )
}

export const AddExploreModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: Omit<ManualContent, '_id'>) => void
  mode: 'edit' | 'create'
  content?: ManualContent
  loading?: boolean
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode,
  content,
  loading,
}) => {
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add Explore' : 'Edit Explore'}
      childrenClassName="md:!max-w-[420px]"
    >
      <AddExploreForm
        onSuccessCallback={onSuccessCallback}
        content={content}
        mode={mode}
        open={open}
        loading={loading}
      />
    </ModalComponent>
  )
}
